/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed,
 * sublicensed, or used without written prior permission from Paxia AI Pty Ltd.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/ErrorOutline';

/**
 * Three visible stages for the supported ABV build flow, derived EXCLUSIVELY
 * from the backend's workflow_status / proposal revision identity. This card
 * never infers workflow state from prose, never auto-sends messages, and holds
 * no authoritative workflow state of its own — it projects backend truth and
 * offers at most ONE ordinary user action per stage boundary.
 */

export type ABVStageKey = 'design' | 'prepare' | 'review' | 'done';

export interface ABVWorkflowStagesProps {
  /** Latest backend workflow_status (authoritative). */
  workflowStatus: string | null | undefined;
  /** Proposal revision the displayed approval would bind (backend identity). */
  proposalRevision: number | null;
  /** Terminal runtime status string when a stage stopped (e.g. translation_blocked). */
  blockedStatus?: string | null;
  /** True while a user-triggered execution is running (blocks duplicate sends). */
  busy?: boolean;
  /** Actual policy facts parsed from the current backend proposal, or null
   *  when that fact is not available — never a cohort default presented as
   *  the approved configuration. */
  policyFacts?: {
    fixedAllocation?: string | null;
    maximum?: string | null;
    rewardRisk?: string | null;
    orderType?: string | null;
    inactiveOnInstall?: boolean | null;
    approvalOn?: boolean | null;
    schedulingDeferred?: boolean | null;
    symbol?: string | null;
    accountId?: string | null;
  } | null;
  /** Fixed operating mode label from the backend operating-profile owner. */
  operatingMode?: string | null;
  /** Direct draft edit of an analysis expert's saved instructions. The
   *  backend saves a new revision of the SAME draft; no model turn. */
  onEditExpert?: (key: string, prompt: string) => void | Promise<void>;
  onPrepareBuild: () => void | Promise<void>;
  onApproveBuild: () => void | Promise<void>;
  onRefresh?: () => void;
  onCancel?: () => void;
  methods?: { key: string; name: string; kind: string; inputs: string[]; prompt: string | null; refreshPolicy?: string | null }[];
}

const STAGE_OF_STATUS: Record<string, ABVStageKey> = {
  // Saved-but-idle: the design exists and a USER preparation request is the
  // next action — stage 1 complete, stage 2 not yet started.
  preparing: 'prepare',
  installing: 'review',
  installation_unverified: 'review',
  execution_failed: 'prepare',
  execution_cancelled: 'prepare',
  status_unavailable: 'design',
  approval_status_unknown: 'review',
  approval_rejected: 'review',
  design_ready_for_translation: 'prepare',
  needs_design_repair: 'design',
  needs_clarification: 'design',
  machine_candidate_ready: 'prepare',
  candidate_review_passed: 'prepare',
  candidate_review_changes_required: 'prepare',
  translation_blocked: 'prepare',
  build_preparation_blocked: 'prepare',
  awaiting_user_approval: 'review',
  architect_builder_validator_built: 'done',
  // An existing claimed build reference does NOT establish successful
  // readback; it renders as a neutral already-claimed state, not completion.
  proposal_already_claimed: 'review',
  proposal_revision_changed: 'review',
  proposal_decision_invalid: 'review',
  operating_selection_changed: 'review',
};

const BLOCKED_STATUSES = new Set([
  'execution_failed', 'execution_cancelled', 'status_unavailable', 'approval_status_unknown', 'approval_rejected',
  'translation_blocked',
  'build_preparation_blocked',
  'needs_design_repair',
  // A review that found changes stopped preparation; it is not "running".
  'candidate_review_changes_required',
  'proposal_revision_changed',
  'proposal_decision_invalid',
  'operating_selection_changed',
]);

const STAGE_INDEX: Record<ABVStageKey, number> = { design: 0, prepare: 1, review: 2, done: 3 };

export function stageForWorkflowStatus(status: string | null | undefined): ABVStageKey {
  if (!status) return 'design';
  return STAGE_OF_STATUS[status] ?? 'design';
}

const BLOCKED_EXPLANATIONS: Record<string, string> = {
  execution_failed: 'The latest operation failed. Installation has not been verified here. Review the execution details before retrying.',
  execution_cancelled: 'The operation was stopped. Saved work is retained; refresh its state before choosing the next action.',
  status_unavailable: 'Current state could not be verified. Refresh the conversation; approval is unavailable until it is current.',
  approval_rejected: 'The approval request was rejected. Refresh and review the current proposal before approving again.',
  approval_status_unknown: 'The approval request could not be confirmed. It may have started: refresh to reconcile its status before submitting anything again.',
  candidate_review_changes_required: 'The review found changes that need attention. Preparation has stopped; review the findings in the conversation.',
  translation_blocked:
    'Preparation stopped: nothing was installed and the saved design is unchanged. Ask for the reported finding to be addressed in the conversation, or revise the design — nothing is running.',
  build_preparation_blocked:
    'The proposal could not be prepared. Nothing was installed. The message above states the backend reason; no automatic retry will occur.',
  needs_design_repair:
    'The design needs correction before it can be prepared. Nothing is installed yet; answer the questions above or adjust your instructions.',
  proposal_revision_changed:
    'The proposal changed while you were viewing it. The previous approval is no longer available — review the new revision before approving.',
  proposal_decision_invalid:
    'That action was not recorded. Re-read the current proposal state above before approving.',
  operating_selection_changed:
    'The account or symbol selection no longer matches the saved design. The displayed proposal belongs to the earlier selection; apply the changed selection to the draft (or switch back) before approving.',
};

const ABVWorkflowStages: React.FC<ABVWorkflowStagesProps> = ({
  workflowStatus,
  proposalRevision,
  blockedStatus,
  busy = false,
  policyFacts,
  onPrepareBuild,
  onApproveBuild,
  onRefresh,
  onCancel,
  methods = [],
  operatingMode = null,
  onEditExpert,
}) => {
  const stage = stageForWorkflowStatus(workflowStatus);
  const blocked = Boolean(blockedStatus && BLOCKED_STATUSES.has(blockedStatus));
  const designSaved = stage !== 'design';
  // Only these mean machine work is actually in flight; the saved-idle state
  // (design_ready_for_translation without busy) is NOT preparation running.
  const preparing = busy && (
    workflowStatus === 'preparing'
    || workflowStatus === 'machine_candidate_ready'
    || workflowStatus === 'candidate_review_passed'
    || workflowStatus === 'design_ready_for_translation');
  const awaitingApproval = workflowStatus === 'awaiting_user_approval';
  const installed = workflowStatus === 'architect_builder_validator_built';
  const alreadyClaimed = workflowStatus === 'proposal_already_claimed';

  // One in-flight guard per action: a double-click can only ever produce one
  // ordinary message. The lock is a ref so a same-tick double dispatch (both
  // clicks before React commits) is also caught; the backend remains the
  // authority, this is UX hygiene on top of it.
  const pendingActionRef = React.useRef<string | null>(null);
  const [pendingAction, setPendingAction] = React.useState<string | null>(null);
  const fire = async (key: 'prepare' | 'approve', fn: () => void | Promise<void>) => {
    if (busy || pendingActionRef.current) return;
    pendingActionRef.current = key;
    setPendingAction(key);
    try { await fn(); } finally { pendingActionRef.current = null; setPendingAction(null); }
  };

  // Direct draft editing: one expert's saved instructions at a time. Saving
  // sends the typed revision request; the backend saves a NEW revision of the
  // same draft and invalidates any affected preparation or approval.
  const [editingExpert, setEditingExpert] = React.useState<string | null>(null);
  const [editDraft, setEditDraft] = React.useState('');
  const [savingEdit, setSavingEdit] = React.useState(false);
  const startEdit = (key: string, prompt: string) => {
    setEditingExpert(key);
    setEditDraft(prompt);
  };
  const saveEdit = async () => {
    if (!editingExpert || !onEditExpert || !editDraft.trim()) return;
    setSavingEdit(true);
    try {
      await onEditExpert(editingExpert, editDraft);
      setEditingExpert(null);
    } finally { setSavingEdit(false); }
  };

  return (
    <Box
      data-testid="abv-workflow-stages"
      sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, mb: 2, backgroundColor: 'background.paper' }}
    >
      <Stepper activeStep={STAGE_INDEX[stage]} alternativeLabel>
        <Step key="design" completed={designSaved}>
          <StepLabel
            error={blocked && stage === 'design'}
            optional={<Typography variant="caption">Design the team</Typography>}
          >
            {designSaved ? 'Design saved' : 'Designing'}
          </StepLabel>
        </Step>
        <Step key="prepare" completed={awaitingApproval || installed}>
          <StepLabel
            error={blocked && stage === 'prepare'}
            optional={<Typography variant="caption">Prepare the build</Typography>}
          >
            {preparing && !blocked ? 'Preparing…' : awaitingApproval || installed ? 'Prepared' : 'Preparation'}
          </StepLabel>
        </Step>
        <Step key="review" completed={installed}>
          <StepLabel
            error={blocked && stage === 'review'}
            optional={<Typography variant="caption">Review and finish</Typography>}
          >
            {installed ? 'Installed' : awaitingApproval ? 'Awaiting your approval' : 'Review'}
          </StepLabel>
        </Step>
      </Stepper>

      {methods.length > 0 && <Box sx={{ mt: 2 }} data-testid="abv-expert-methods">
        <Typography variant="subtitle2">Expert flow and instructions</Typography>
        {['analysis', 'native'].map(group => <Box key={group} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">{group === 'analysis' ? 'Analytical experts' : 'Native safeguards and execution'}</Typography>
          {methods.filter(item => group === 'analysis' ? item.kind === 'analysis' : item.kind !== 'analysis').map(item => (
            <details key={item.key} style={{ marginTop: 6 }} data-testid={`abv-expert-${item.key}`}>
              <summary style={{ cursor: 'pointer' }}>{item.name}</summary>
              <Typography variant="caption" component="div">Receives: {item.inputs.join(', ') || 'No declared inputs'}</Typography>
              {item.refreshPolicy && (
                <Typography variant="caption" component="div" data-testid={`abv-expert-refresh-${item.key}`}>
                  Model call: {item.refreshPolicy}; otherwise its last result is reused with provenance.
                </Typography>
              )}
              {editingExpert === item.key ? (
                <Box sx={{ mt: 1 }} data-testid={`abv-expert-edit-${item.key}`}>
                  <TextField
                    multiline
                    minRows={4}
                    fullWidth
                    size="small"
                    label="Draft instructions (saved as a new design revision)"
                    value={editDraft}
                    onChange={event => setEditDraft(event.target.value)}
                    disabled={savingEdit}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={savingEdit || busy || !editDraft.trim()}
                      onClick={() => { void saveEdit(); }}
                      data-testid={`abv-expert-save-${item.key}`}
                    >
                      {savingEdit ? 'Saving…' : 'Save draft edit'}
                    </Button>
                    <Button size="small" disabled={savingEdit} onClick={() => setEditingExpert(null)}>Cancel</Button>
                  </Box>
                </Box>
              ) : (
                <>
                  <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.prompt ?? 'Native platform implementation; no analytical prompt.'}</Typography>
                  {group === 'analysis' && item.prompt && onEditExpert ? (
                    <Button
                      size="small"
                      sx={{ mt: 0.5 }}
                      disabled={busy}
                      onClick={() => startEdit(item.key, item.prompt)}
                      data-testid={`abv-expert-edit-button-${item.key}`}
                    >
                      Edit instructions
                    </Button>
                  ) : null}
                </>
              )}
            </details>
          ))}
        </Box>)}
      </Box>}
      {workflowStatus === 'installing' && <Box sx={{ display: 'flex', gap: 1, mt: 2 }} data-testid="abv-installing">
        <CircularProgress size={18} /><Typography variant="body2">Installation is in progress. Completion will be shown only after backend readback.</Typography>
      </Box>}
      {(busy || blocked || workflowStatus === 'installation_unverified' || alreadyClaimed) && <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        {onCancel && <Button onClick={onCancel} size="small">Stop operation</Button>}
        {onRefresh && <Button onClick={onRefresh} size="small">Refresh state</Button>}
      </Box>}
      {workflowStatus === 'installation_unverified' && <Alert severity="warning" sx={{ mt: 1 }}>Installation has not been verified by backend readback.</Alert>}

      {/* Stage 1 — Design the team. Without a saved-design status there is
          nothing to prepare: the action only appears once the backend reports a
          saved design (design_ready_for_translation, not busy). */}
      {stage === 'design' && !blocked && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
            {workflowStatus
              ? 'The design needs correction before it can be prepared — answer the questions above in the conversation.'
              : <>Describe your system in the conversation. A saved design here is <strong>not installed</strong> —
                 nothing runs until you explicitly approve a reviewed proposal later.</>}
          </Typography>
          {workflowStatus ? null : null}
        </Box>
      )}
      {workflowStatus === 'design_ready_for_translation' && !busy && !blocked && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
            Your design is saved and waiting — nothing is running and nothing is installed yet. Preparation
            starts only when you ask for it.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={busy || pendingAction !== null}
            onClick={() => { void fire('prepare', onPrepareBuild); }}
            data-testid="abv-prepare-build"
          >
            Prepare build
          </Button>
        </Box>
      )}

      {/* Stage 2 — Prepare the build (real activity; no fake percentages) */}
      {preparing && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          {preparing ? <CircularProgress size={18} /> : null}
          <Typography variant="body2" color="text.secondary">
            {blocked
              ? null
              : 'Backend preparation is running: translation, fidelity review and proposal assembly advance automatically. Reloading reads the current backend state.'}
          </Typography>
        </Box>
      )}

      {/* Blocked / stopped explanation — never only a raw status string */}
      {blocked && (
        <Alert
          severity="warning"
          icon={<ErrorIcon fontSize="small" />}
          sx={{ mt: 2 }}
          data-testid="abv-blocked-explanation"
        >
          <Typography variant="body2">
            {BLOCKED_EXPLANATIONS[blockedStatus ?? ''] ??
              'Preparation stopped. Nothing was installed; the message above states the backend reason.'}
          </Typography>
          {blockedStatus ? (
            <Chip size="small" label={blockedStatus} sx={{ mt: 1, height: 20, fontSize: '0.65rem' }} />
          ) : null}
        </Alert>
      )}

      {/* Stage 3 — Review and finish: concrete policy facts + revision identity */}
      {awaitingApproval && !blocked && (
        <Box sx={{ mt: 2 }} data-testid="abv-review-summary">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }} data-testid="abv-policy-facts">
            {operatingMode
              ? <Chip size="small" color="primary" label={`Mode: ${operatingMode}`} data-testid="abv-mode-fact" />
              : null}
            {policyFacts?.symbol ? <Chip size="small" variant="outlined" label={policyFacts.symbol} /> : null}
            {policyFacts?.accountId ? <Chip size="small" variant="outlined" label={policyFacts.accountId} /> : null}
            {policyFacts?.fixedAllocation
              ? <Chip size="small" color="primary" variant="outlined" label={policyFacts.fixedAllocation} data-testid="abv-fixed-allocation" />
              : <Chip size="small" variant="outlined" color="warning" label="Fixed allocation: unavailable in this proposal" data-testid="abv-fixed-unavailable" />}
            {policyFacts?.maximum
              ? <Chip size="small" variant="outlined" label={policyFacts.maximum} />
              : <Chip size="small" variant="outlined" color="warning" label="Maximum risk: unavailable" />}
            {policyFacts?.rewardRisk
              ? <Chip size="small" variant="outlined" label={policyFacts.rewardRisk} />
              : <Chip size="small" variant="outlined" color="warning" label="Reward:risk minimum: unavailable" />}
            {policyFacts?.orderType
              ? <Chip size="small" variant="outlined" label={policyFacts.orderType} />
              : null}
            <Chip size="small" variant="outlined" label={policyFacts?.inactiveOnInstall == null ? 'Install status: unavailable' : policyFacts.inactiveOnInstall ? 'Inactive on install' : 'Active on install'} />
            <Chip size="small" variant="outlined" label={policyFacts?.approvalOn == null ? 'Approval mode: unavailable' : policyFacts.approvalOn ? 'Trade approval: ON' : 'Trade approval: OFF'} />
            <Chip size="small" variant="outlined" label={policyFacts?.schedulingDeferred == null ? 'Scheduling: unavailable' : policyFacts.schedulingDeferred ? 'Scheduling: deferred' : 'Scheduling: configured'} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Reviewing proposal revision{' '}
              <strong data-testid="abv-proposal-revision">{proposalRevision ?? '—'}</strong>. Approving installs exactly
              this revision using the settings shown above. This action does not run the system.
            </Typography>
            <Tooltip title={proposalRevision == null ? 'Waiting for the proposal reference' : 'Send one ordinary approval message'}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={busy || pendingAction !== null || proposalRevision == null}
                  onClick={() => { void fire('approve', onApproveBuild); }}
                  data-testid="abv-approve-install"
                >
                  Approve and install
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Already-claimed: a build reference exists; this UI cannot verify its
          readback outcome, so it states that honestly instead of claiming
          installed success. */}
      {alreadyClaimed && !installed && (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="abv-already-claimed">
          <Typography variant="body2">
            A build for this proposal was already claimed. Its installation outcome is not verified in this
            view — check the system view for the actual installed state before relying on it.
          </Typography>
        </Alert>
      )}

      {/* Completion — only after the backend reports an installed build */}
      {installed && (
        <Alert severity="success" icon={<CheckCircleIcon fontSize="small" />} sx={{ mt: 2 }} data-testid="abv-installed">
          <Typography variant="body2">
            Installation confirmed by backend readback. Runtime and scheduling require separate actions.
            The full graph and prompts are available in the system view.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ABVWorkflowStages;
